/* eslint @typescript-eslint/explicit-function-return-type: 1, @typescript-eslint/no-explicit-any: 1 -- TODO fix types */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { AuthorizationService, sanitizeUrl, SentryService, UserService } from '@hosty-app/services';
import { isAuthError } from '@hosty-app/utils';

/** Emitted instead of a token when the refresh could not be completed. */
const REFRESH_FAILED = 'REFRESH_FAILED' as const;

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  /**
   * The in-flight refresh, shared by every request waiting on it.
   *
   * This used to be a link in the pipe of whichever request happened to 401
   * first, so `finalize` ran on unsubscribe: when that request was cancelled — a
   * `switchMap` in an effect, a destroyed component, a superseded navigation —
   * the refresh HTTP call was cancelled with it and every queued request was
   * failed, even though the token was perfectly refreshable. Owning it here and
   * sharing it decouples the refresh from any single subscriber.
   */
  private refresh$: Observable<string> | null = null;

  constructor(
    public authorizationService: AuthorizationService,
    public userService: UserService,
    public router: Router,
    private sentry: SentryService,
  ) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (request.url.includes('refresh') || request.url.includes('login')) {
          // Only wipe tokens and sign the user out when the refresh endpoint
          // itself rejected the token. Network failures (status 0) happen
          // routinely on mobile and must leave the session intact.
          if (request.url.includes('refresh') && isAuthError(error)) {
            // Report BEFORE wiping. This is the only code path in the app that
            // signs a user out on its own, and it was previously invisible:
            // the error is handled, so it never reached Angular's ErrorHandler
            // and never reached Sentry. Every "the app logged me out again"
            // report had to be diagnosed by guesswork.
            this.sentry.logEvent('Session dropped: refresh token rejected', {
              status: error.status,
              url: sanitizeUrl(error.url),
              hadRefreshToken: !!this.authorizationService.refreshToken,
            });
            this.authorizationService.cleanTokens();
            this.router.navigateByUrl('/login');
          }
          return throwError(error);
        }

        if (error.status !== 401) {
          return throwError(error);
        }

        return this.refreshToken().pipe(
          switchMap((token) =>
            token === REFRESH_FAILED
              ? throwError(error)
              : next.handle(this.addAuthenticationToken(request)),
          ),
        );
      }),
    );
  }

  /**
   * Emits the new access token, or REFRESH_FAILED if it could not be obtained.
   * Concurrent callers share a single refresh request.
   */
  private refreshToken(): Observable<string> {
    if (this.refresh$) {
      return this.refresh$;
    }

    this.refresh$ = this.userService.refreshToken(this.authorizationService.refreshToken).pipe(
      // Deliberately NOT retried.
      //
      // The endpoint returns a new refresh_token on every call, i.e. it rotates,
      // which means the token we just sent is spent whether or not we saw the
      // response. Retrying re-sends a consumed token, and the server answers
      // 401 — which this interceptor treats as "the session is invalid" and
      // wipes the credentials. So on a flaky connection the retry manufactures
      // the very logout it was added (in eea2c999) to prevent.
      //
      // status 0 is routine here: Sentry shows it constantly on this app, since
      // iOS kills in-flight requests whenever it suspends the webview. A refresh
      // must therefore be attempted exactly once. If it fails at the network
      // level the tokens are left intact and authGuard shows the
      // connection-problem screen, from which the user can retry.
      tap((tokens) => {
        if (!tokens?.token || !tokens?.refresh_token) {
          // Storing a missing value would write the string "undefined" into
          // localStorage and poison every subsequent refresh.
          throw new Error('Refresh response missing token or refresh_token');
        }
        this.authorizationService.setTokens(tokens.token, tokens.refresh_token);
      }),
      map((tokens): string => tokens.token),
      // Queued requests must unblock and fail cleanly rather than hang forever.
      catchError(() => of<string>(REFRESH_FAILED)),
      // Release once settled so a later 401 starts a fresh refresh.
      finalize(() => {
        this.refresh$ = null;
      }),
      // Keeps the request alive independently of any single subscriber and
      // replays the outcome to requests that queue up while it is in flight.
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refresh$;
  }

  addAuthenticationToken(request) {
    const accessToken = this.authorizationService.jwtToken;
    if (!accessToken) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }
}
